import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./popup_sku_release.scoped.scss";

interface State {
}

const defState = {
};

interface SearchParams {
}

const defSearchParams = {
};

interface Result {
  infos: any[]
}

const defResult = {
  infos: []
};

const validationMsg = [
  {id: "holdYn", type: "InvalidStatus", msg: "작업대에 SKU가 없을 때만 해제할 수 있습니다."}
];

function PopupSkuRelease(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const gridStateKey = "/inoutbound/popup_sku_release";
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(gridStateKey), []);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    {
      headerName: "보류해제", field: "", width: 80, cellStyle: kutil.aggridButtonCellStyle,
      cellRenderer: (p: any) => {
        if (!props.data.skuNo) {
          if (!auth.write) return null;
          return (<button className="icon-button grid-add" onClick={() => clearSku(p.data)}>해제</button>);
        }
      }
    },
    { headerName: "작업구분", field: "workType", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("workType") },
    { headerName: "창고", field: "wh", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "SKU No", field: "skuNoEx", width: 100, cellStyle: {textAlign: "center"} },
    { headerName: "SKU Type", field: "skuType", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("skuType") },
    { headerName: "SKU Size", field: "skuSize", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("skuSize") },
    { headerName: "SKU Cell", field: "skuCellQty", width: 100, cellStyle: {textAlign: "center"} },
    { headerName: "Stock Cell", headerClass: "hd-ta-right", field: "skuCellUseQty", width: 100, cellStyle: {textAlign: "right"} },
    { headerName: "Stock Qty", headerClass: "hd-ta-right", field: "stockQty", width: 100, cellStyle: {textAlign: "right"} },
    { headerName: "보류일시", field: "holdDate", width: 200, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss") },
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks = useStableHandlers({
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, gridStateKey);
    }
  });

  useEffect(() => {
    init();
  }, []);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const data = await kutil.sendGet("/api/inoutbound/workstation_manage.data?mode=searchSkuHold", {workStationId: props.data.workStationId}, {});
      setResult({
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function closePopup() {
    props.onClose();
  }

  async function clearSku(info: any) {
    if (await kutil.confirm("해제하시겠습니까?") == false) {
      return false;
    }

    try {
      info.workStationId = props.data.workStationId
      const data = await kutil.sendPost("/api/inoutbound/workstation_manage.proc?mode=clearSku", info, {
        validationMsg: validationMsg
      });
      await kutil.alert("해제하였습니다.");
      props.onClose({isSuccess: true});
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* 팝업 */}
      <Dialog
        sx={{"& .MuiDialog-container": {"& .MuiPaper-root": {width: "100%", maxWidth: "1100px",},},}}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          SKU 보류 해제
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={(theme) => ({
            position: "absolute",
            right: 36,
            top: 24,
            color: theme.palette.grey[500],
          })}
        >
        </IconButton>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            <div>
            </div>
            {/* 그리드 */}
            <AgGridProvider modules={modules}>
              <div className="grid-area">
                <CustomAgGridReact
                  columnDefs={gridInfo}
                  rowData={result.infos}
                  defaultColDef={kutil.defaultColDef}
                  initialState={gridState}
                  onStateUpdated={gridCallbacks.onStateUpdated}
                />
              </div>
            </AgGridProvider>
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}

export default React.memo(PopupSkuRelease);
